<div class="space-y-7">

  <!-- Error: the server's own message, shown as-is — never replaced. -->
  <div *ngIf="error()" role="alert" class="uw-callout uw-callout-danger uw-fade">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16h.01"/></svg>
    <span class="flex-1">{{ error() }}</span>
    <!-- Only when nothing has loaded yet — the page is empty below this, and "Try again" can mean only
         one thing. After a failed mutation the list is still on screen, and a retry here would be
         ambiguous about WHAT it retries. -->
    <button *ngIf="!switches() && !loading()" type="button" (click)="reload()"
            class="shrink-0 font-semibold underline underline-offset-2 cursor-pointer">Try again</button>
  </div>

  <!-- Loading: a skeleton, never a spinner and never a blank page. -->
  <div *ngIf="loading()" class="space-y-6" aria-hidden="true">
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div *ngFor="let i of [1,2,3]" class="uw-skel h-[70px]"></div>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div *ngFor="let i of [1,2]" class="uw-skel h-[124px]"></div>
    </div>
    <div class="space-y-2.5">
      <div class="uw-skel h-[52px]"></div>
      <div *ngFor="let i of [1,2,3]" class="uw-skel h-[66px]"></div>
    </div>
  </div>

  <ng-container *ngIf="!loading() && switches() as s">

    <!-- ═══ At a glance — the headline numbers, all from workspaceTotals (workspaces.util.ts) ═══ -->
    <div *ngIf="totals() as t" class="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div class="uw-card uw-stat uw-rise">
        <div class="uw-stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-[18px] h-[18px]" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4M10 10h4M10 14h4M10 18h4"/></svg>
        </div>
        <div class="min-w-0">
          <p class="uw-stat-label">Workspaces</p>
          <p class="uw-stat-value">{{ t.workspaces }}</p>
        </div>
      </div>
      <div class="uw-card uw-stat uw-rise" style="animation-delay: 40ms">
        <div class="uw-stat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-[18px] h-[18px]" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <div class="min-w-0">
          <p class="uw-stat-label">People holding a seat</p>
          <p class="uw-stat-value">{{ t.people }}</p>
        </div>
      </div>
      <div class="uw-card uw-stat uw-rise" style="animation-delay: 80ms">
        <!-- A status, so it never rests on colour alone: the icon changes with it, and the note says it. -->
        <div class="uw-stat-icon" [class.is-warn]="t.atCapacity > 0">
          <svg *ngIf="t.atCapacity" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-[18px] h-[18px]" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></svg>
          <svg *ngIf="!t.atCapacity" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-[18px] h-[18px]" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h4l3-8 4 16 3-8h4"/></svg>
        </div>
        <div class="min-w-0">
          <p class="uw-stat-label">At capacity</p>
          <p class="flex items-baseline gap-2 min-w-0">
            <span class="uw-stat-value">{{ t.atCapacity }}</span>
            <span class="uw-stat-note truncate">{{ t.capped ? ('of ' + t.capped + ' with a seat limit') : 'No seat limits set' }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- ═══ The way in — the two project switches: the app's front door ═══ -->
    <section>
      <div class="flex items-center gap-2 mb-3">
        <h2 class="uw-h2">The way in</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">

        <!-- Allow sign-ups -->
        <div class="uw-card p-4 uw-rise" style="animation-delay: 120ms">
          <div class="flex items-start gap-3.5">
            <!-- The door itself: open while people can come in this way, shut when they can't. -->
            <div class="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center border transition-colors duration-300"
                 [ngClass]="signupsOpen(s) ? 'text-emerald-500 bg-emerald-500/10 border-emerald-500/25' : 'text-(--uw-faint) bg-(--uw-hover) border-(--uw-line)'">
              <svg *ngIf="signupsOpen(s)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M13 4h3a2 2 0 0 1 2 2v14"/><path d="M2 20h3"/><path d="M13 20h9"/><path d="M10 12v.01"/><path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z"/></svg>
              <svg *ngIf="!signupsOpen(s)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M18 20V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v14"/><path d="M2 20h20"/><path d="M14 12v.01"/></svg>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-3">
                <p class="text-[13px] font-semibold text-(--uw-strong)">Allow sign-ups</p>
                <div class="flex items-center gap-2.5">
                  <span class="uw-switch-state" [class.is-on]="signupsOpen(s) && busy() !== 'signups'">{{ busy() === 'signups' ? 'Saving…' : (signupsOpen(s) ? 'On' : 'Off') }}</span>
                  <button type="button" role="switch" class="uw-switch" [class.is-busy]="busy() === 'signups'"
                          [attr.aria-checked]="signupsOpen(s)" aria-label="Allow sign-ups"
                          (click)="flipProject('signups', !signupsOpen(s))"
                          [disabled]="!switchControl.enabled || busy() === 'signups'"
                          [attr.title]="switchControl.enabled ? null : switchControl.reason"></button>
                </div>
              </div>
              <p class="text-xs text-(--uw-muted) mt-1 leading-relaxed">
                People can create their own account from your app's sign-in page. Turning this off makes the app invitation-only. It applies in Developer mode too.
              </p>
              <!-- Never a tooltip alone: a disabled switch says why, beside itself. -->
              <p *ngIf="!switchControl.enabled" class="uw-reason mt-2.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                <span>{{ switchControl.reason }}</span>
              </p>
              <p *ngIf="signupsAuditLine(s)" class="uw-audit mt-3 pt-2.5 border-t border-(--uw-line)">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                {{ signupsAuditLine(s) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Allow invitations -->
        <div class="uw-card p-4 uw-rise" style="animation-delay: 160ms">
          <div class="flex items-start gap-3.5">
            <div class="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center border transition-colors duration-300"
                 [ngClass]="invitesOpen(s) ? 'text-emerald-500 bg-emerald-500/10 border-emerald-500/25' : 'text-(--uw-faint) bg-(--uw-hover) border-(--uw-line)'">
              <svg *ngIf="invitesOpen(s)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              <svg *ngIf="!invitesOpen(s)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10.5V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h9"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><path d="m17 17 4 4M21 17l-4 4"/></svg>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-3">
                <p class="text-[13px] font-semibold text-(--uw-strong)">Allow invitations</p>
                <div class="flex items-center gap-2.5">
                  <span class="uw-switch-state" [class.is-on]="invitesOpen(s) && busy() !== 'invites'">{{ busy() === 'invites' ? 'Saving…' : (invitesOpen(s) ? 'On' : 'Off') }}</span>
                  <button type="button" role="switch" class="uw-switch" [class.is-busy]="busy() === 'invites'"
                          [attr.aria-checked]="invitesOpen(s)" aria-label="Allow invitations"
                          (click)="flipProject('invites', !invitesOpen(s))"
                          [disabled]="!switchControl.enabled || busy() === 'invites'"
                          [attr.title]="switchControl.enabled ? null : switchControl.reason"></button>
                </div>
              </div>
              <p class="text-xs text-(--uw-muted) mt-1 leading-relaxed">
                You and your workspace admins can invite people by email. Turning this off leaves a username and password you create as the only way in.
              </p>
              <p *ngIf="!switchControl.enabled" class="uw-reason mt-2.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
                <span>{{ switchControl.reason }}</span>
              </p>
              <p *ngIf="invitesAuditLine(s)" class="uw-audit mt-3 pt-2.5 border-t border-(--uw-line)">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                {{ invitesAuditLine(s) }}
              </p>
            </div>
          </div>
        </div>

        <!-- How phones reach this app. A third door beside the two above, but a choice of three rather than
             an on/off, so it is a radio group and not a switch. -->
        <div class="uw-card p-4 uw-rise" style="animation-delay: 160ms">
          <p class="text-[13px] font-semibold text-(--uw-strong)">How phones open this app</p>
          <div class="mt-3 space-y-2" role="radiogroup" aria-label="How phones open this app">
            <button *ngFor="let m of modes" type="button" role="radio"
                    [attr.aria-checked]="mobileAccess()?.mode === m"
                    [disabled]="!switchControl.enabled || busy() === 'mobileAccess'"
                    (click)="chooseMode(m)"
                    class="w-full text-left rounded-lg border p-3 transition-colors"
                    [ngClass]="mobileAccess()?.mode === m
                      ? 'border-(--uw-strong) bg-(--uw-hover)' : 'border-(--uw-line)'">
              <span class="text-[13px] font-medium text-(--uw-strong)">{{ modeLabels[m] }}</span>
              <span class="block text-xs text-(--uw-muted) mt-1 leading-relaxed">{{ modeNotes[m] }}</span>
            </button>
          </div>

          <!-- Asked before it is applied: this is the one choice that takes the app away from someone. -->
          <div *ngIf="pendingMode() === 'native_only'" class="uw-reason mt-3 flex-col items-start gap-2">
            <span>{{ nativeOnlyWarning }}</span>
            <span class="flex gap-2">
              <button type="button" (click)="applyMode('native_only')"
                      class="rounded-md bg-(--uw-strong) text-white text-xs px-3 py-1.5">Switch anyway</button>
              <button type="button" (click)="pendingMode.set(null)"
                      class="rounded-md border border-(--uw-line) text-xs px-3 py-1.5">Cancel</button>
            </span>
          </div>

          <p *ngIf="!switchControl.enabled" class="uw-reason mt-2.5"><span>{{ switchControl.reason }}</span></p>
        </div>
      </div>
    </section>

    <!-- ═══ The workspaces ═══ -->
    <section>
      <div class="flex items-center justify-between gap-3 mb-3 min-h-[20px]">
        <div class="flex items-center gap-2">
          <h2 class="uw-h2">Workspaces</h2>
          <span class="uw-count">{{ workspaces().length }}</span>
        </div>
        <div class="flex items-center gap-3">
          <!-- A mutation refresh keeps the panel mounted (Finding 7) — this quiet line is the only
               sign of it, instead of the whole panel flashing back to the skeleton. -->
          <p *ngIf="refreshing()" class="uw-live" aria-live="polite"><i></i>Refreshing…</p>

          <!-- Task 11: a non-empty search loads every workspace's people once, in the background — this
               quiet line is the only sign of that round trip. -->
          <p *ngIf="searching()" class="uw-live" aria-live="polite"><i></i>Searching…</p>
        </div>
      </div>

      <!-- Fix-round-1 Critical 2: a workspace whose load failed is not a workspace that was checked
           and found empty — this says so alongside whatever the search still managed to show, so a
           confident "nothing matches" (or a confident partial result) is never built on a request that
           never actually looked. -->
      <p *ngIf="searchLoadErrorMessage() as msg" class="uw-callout uw-callout-warn uw-fade mb-3" aria-live="polite">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></svg>
        <span>{{ msg }}</span>
      </p>

      <!-- ═══ Create a workspace — a single field above the list, not a modal ═══ -->
      <div class="uw-composer-wrap mb-3 uw-rise" style="animation-delay: 200ms">
        <div class="uw-composer">
          <div class="uw-composer-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2.25" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
          </div>
          <input type="text" [(ngModel)]="newWorkspaceName" (keyup.enter)="createWorkspace()" maxlength="60"
                 [disabled]="creatingWorkspace()" placeholder="New workspace name" aria-label="New workspace name" />
          <button type="button" (click)="createWorkspace()" [disabled]="creatingWorkspace() || !newWorkspaceName.trim()"
                  class="uw-btn uw-btn-primary h-9 px-4 text-xs">
            {{ creatingWorkspace() ? 'Creating…' : 'Create workspace' }}
          </button>
        </div>
        <!-- What happens next — said while a name is being typed, not permanently under the field. -->
        <p class="uw-composer-hint" [class.is-shown]="!!newWorkspaceName.trim()">
          A new workspace starts empty. Invite someone or add a username and password, then open their role on their row and choose Admin, so they can manage it from inside your app.
        </p>
        <p *ngIf="createError()" class="uw-callout uw-callout-danger mt-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16h.01"/></svg>
          <span>{{ createError() }}</span>
        </p>
      </div>

      <!-- ═══ The workspace list — every workspace, collapsed by default ═══ -->
      <div class="space-y-2.5">
        <!-- Task 11's no-results state — every row hidden by the current search. Never an empty page. -->
        <div *ngIf="noResults()" class="uw-empty uw-fade">
          <div class="uw-empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5M8.5 8.5l5 5M13.5 8.5l-5 5"/></svg>
          </div>
          <p class="text-sm font-medium text-(--uw-text)">{{ noResultsMessage() }}</p>
          <button type="button" (click)="clearSearch.emit()" class="uw-btn uw-btn-quiet mt-4 h-8 px-3.5 text-xs">
            Clear search
          </button>
        </div>

        <ng-container *ngFor="let w of workspaces(); trackBy: trackById; let i = index">
          <!-- fix-round-1 #8: `[search]` removed — nothing in workspace-row.component.html reads it;
               its only effect was re-entering the row's own ngOnChanges on every keystroke and
               provoking a duplicate loadUsers()/loadInvitations() call. -->
          <app-workspace-row *ngIf="isVisible(w.id)" class="block uw-rise" [style.animation-delay.ms]="rowDelay(i)"
                              [workspace]="w" [access]="access"
                              [open]="isOpen(w.id)" [switches]="switches()" [developerModeOn]="developerModeOn()"
                              [countLabel]="countLabelFor(w.id)" [matchedUserIds]="matchedUserIdsFor(w.id)"
                              [searchFailed]="searchFailedFor(w.id)" [roleNames]="roleNames()"
                              (toggle)="toggleOpen(w.id)"
                              (changed)="reload({ silent: true })" />
        </ng-container>

        <!-- Written empty state: this can only mean "nothing besides the builders' workspace yet" —
             Default (Editor) always exists, so the list is never truly empty — but the bound is
             `<= 1`, not `=== 1`, so a genuinely empty response (which should never happen) still
             gets this written state rather than a blank box. Suppressed while a search is running its
             own no-results state above — the two must never both claim the list is empty for
             different reasons at once. -->
        <div *ngIf="workspaces().length <= 1 && !noResults()" class="uw-empty">
          <div class="uw-empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-5 h-5" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4M10 10h4M10 14h4M10 18h4"/></svg>
          </div>
          <p class="text-sm text-(--uw-muted) max-w-sm">Only the builders' workspace so far. Add one when you have a group of users to keep separate.</p>
        </div>
      </div>
    </section>
  </ng-container>
</div>
